import { SerializedData, Store } from "bernie-core";
import { action, computed, observable } from "mobx";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { BexApiService } from "src/services/BexApiService";
import { getBexApiContext } from "bernie-context";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import {
  CustomerPropertySearchByIdQuery,
  CustomerSearchHistoryQuery,
  DateInput,
  HistoryType,
  MapPropertySearchById,
  MapPropertySearchByLatLong,
  OperationResult,
  PropertyDateRangeInput,
} from "__generated__/api";
import MapPropertySearchByIdProperties = MapPropertySearchById.Properties;
import { getNextFriday } from "src/components/utility/DateUtils";
import { MapPlace } from "uitk-react-experimental-module-map";
import { formatText } from "bernie-l10n";
import { FAILED_HOTEL_FETCH } from "src/config/systemEvents";
import { NewPriceItems, RecentlyViewedHotel } from "src/components/flexComponents/RecentlyViewedHotels/typings";
import Items = CustomerSearchHistoryQuery.Items;
import Properties = CustomerPropertySearchByIdQuery.Properties;
import StartDate = CustomerSearchHistoryQuery.StartDate;
import EndDate = CustomerSearchHistoryQuery.EndDate;
import HistoryPropertyDetailInlineFragment = CustomerSearchHistoryQuery.HistoryPropertyDetailInlineFragment;
import PropertySearch = MapPropertySearchByLatLong.PropertySearch;

export enum VariationType {
  PRICE_INCREASE = "increase",
  PRICE_DECREASE = "decrease",
  AVAILABLE = "available",
  SOLD_OUT = "sold_out",
}

export class HotelsStore extends Store {
  @observable
  public filteredHotels: Hotel[] | null = null;

  @observable
  public hasOverfilteredMessage: Boolean = false;

  @observable
  public allHotelsOnMap: MapPlace[] = [];

  @observable
  public isLoading = false;

  @observable
  public isLoadingFilterHotels = false;

  @observable
  public hasMapErrorMessage = false;

  public constructor(state: SerializedData = {}, logger: Logger = NOOP_LOGGER, private bexApiService: BexApiService) {
    super(state, logger);
    this.bexApiService = bexApiService;
  }

  /**
   * Set recently viewed hotels and hotels
   * Add infositeURL and newSearchPrice to recentViewedHotel object
   * newSearchPrice is the hotel price and originalSearchPrice is the recently view hotel price
   * @param {RecentlyViewedHotel[]} recentlyViewedHotels - Array of recently hotels viewed by the user
   * @param {CustomerPropertySearchByIdQuery.Properties[]} freshHotelData - Array of hotels that match with the recently view hotel
   * @memberof HotelsStore
   */
  private getFreshRecentlyViewedHotels = (
    recentlyViewedHotels: Items[],
    freshHotelData: OperationResult<CustomerPropertySearchByIdQuery.Query>[]
  ): RecentlyViewedHotel[] => {
    // facilitate our lives by building map of fresh hotel data with id-startDate-endDate key
    const buildDateKey = (date: StartDate | EndDate | DateInput) => `${date.year}-${date.month}-${date.day}`;
    const buildKey = (
      id: string,
      checkInDate: StartDate | EndDate | DateInput,
      checkOutDate: StartDate | EndDate | DateInput
    ) => `${id}-${buildDateKey(checkInDate)}-${buildDateKey(checkOutDate)}`;

    const mapOfFreshHotelData: { [key: string]: Properties } = freshHotelData
      .filter((result) => result?.data?.propertySearch)
      .map((result) => result.data)
      .reduce((acc, data) => {
        const { propertySearch } = data;
        const mapOfProperties = propertySearch.properties.reduce(
          (acc2, property) => ({
            ...acc2,
            [buildKey(
              property.id,
              propertySearch.searchCriteria.dateRange.checkInDate,
              propertySearch.searchCriteria.dateRange.checkOutDate
            )]: property,
          }),
          {}
        );

        return {
          ...acc,
          ...mapOfProperties,
        };
      }, {});

    const getFreshHotel = (recentlyViewedHotel: Items) =>
      mapOfFreshHotelData[
        buildKey(
          (recentlyViewedHotel.productInfo as HistoryPropertyDetailInlineFragment)?.propertyId || "",
          recentlyViewedHotel.startDate,
          recentlyViewedHotel.endDate
        )
      ];

    return recentlyViewedHotels.filter(getFreshHotel).map((recentlyViewedHotel) => {
      // get fresh hotel
      const freshHotel = getFreshHotel(recentlyViewedHotel);

      // check for price variation
      const firstPriceOption = freshHotel.price?.options && freshHotel.price?.options[0];
      const priceVariation = this.getPriceVariation(recentlyViewedHotel, firstPriceOption);

      return {
        ...recentlyViewedHotel,
        ...priceVariation,
        hisUrl: freshHotel.infositeURL,
      };
    });
  };

  /**
   * Add a new property to recently viewed hotel's object regarding price variation
   */
  private getPriceVariation = (
    hotelFromCustomerSearchHistory: CustomerSearchHistoryQuery.Items & NewPriceItems,
    updatedPrice?: CustomerPropertySearchByIdQuery.Options | null
  ): NewPriceItems => {
    const { originalSearchPrice } = hotelFromCustomerSearchHistory;
    const originalPrice = originalSearchPrice ? Math.round(originalSearchPrice.amount) : 0;
    const currentPrice = updatedPrice?.displayPrice ? Math.round(updatedPrice.displayPrice.amount) : 0;

    // if no new pricing then it is sold out
    if (!currentPrice) {
      return {
        priceChange: VariationType.SOLD_OUT,
      };
    }

    const newPriceItem: NewPriceItems = {};

    // Is price decreased
    if (originalPrice > currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_DECREASE;
    }

    // Is increased
    if (originalPrice < currentPrice) {
      newPriceItem.priceChange = VariationType.PRICE_INCREASE;
    }

    // Is now available
    if (!originalPrice && currentPrice) {
      newPriceItem.priceChange = VariationType.AVAILABLE;
    }

    // Set new price
    if (currentPrice) {
      newPriceItem.newPrice = currentPrice;
      newPriceItem.newPriceFormatted = updatedPrice?.displayPrice?.formatted;
    }

    return newPriceItem;
  };

  @action
  public setAllHotelsOnMapOnSuccessResponse = (
    hotelsFromResponse: MapPropertySearchByIdProperties[],
    locationId: string
  ) => {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const newHotels: MapPlace[] = [];

    for (const property of hotelsFromResponse) {
      // Check if the property has all the necessary info to be displayed on the map
      if (!property.mapMarker || !property.mapMarker.latLong || !property.infositeURL) {
        continue;
      }

      const formattedPrice = property?.price?.lead?.formatted;
      const priceAmount = property?.price?.lead?.amount;

      const newHotel: MapPlace = {
        id: property.id,
        placeName: property.name,
        formattedPrice: priceAmount ? formattedPrice : "",
        latitude: property.mapMarker.latLong.latitude,
        longitude: property.mapMarker.latLong.longitude,
        overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", property.reviews.score),
        reviewCount: `(${formatText("maps.sheets.hotel.reviews.text", property.reviews.total)})`,
        localizedSubtitle: property.reviews.localizedSubtitle,
        imageUrl: property.image && property.image.url,
        hrefUrl: this.constructPinnedHSRUrl(locationId, property.id, checkInDate, checkOutDate),
        icon: {
          type: priceAmount ? "price" : "soldOut",
        },
      };

      newHotels.push(newHotel);
    }

    this.allHotelsOnMap = newHotels;
  };

  @action
  public setAllHotelsOnMapOnFailureResponse = (
    hotels: Hotel[],
    contextStore: ExtendedContextStore,
    locationId: string
  ) => {
    this.allHotelsOnMap.push(...hotels.map((h) => this.mapFlexHotelToPropertyForMap(h, contextStore, locationId)));
  };

  public async fetchUpdatedRecentlyViewedHotels(recentlyViewedHotels: Items[], contextStore: ExtendedContextStore) {
    // group by start date - end date
    const buildDateKey = (hotel: Items) => `${hotel.startDate?.raw}-${hotel.endDate?.raw}`;
    const hotelsGroupedByDates: {
      [key: string]: RecentlyViewedHotel[];
    } = recentlyViewedHotels
      .filter((item) => item !== null)
      .reduce(
        (acc, hotel) => ({
          ...acc,
          [buildDateKey(hotel)]: (acc[buildDateKey(hotel)] || []).concat(hotel),
        }),
        {}
      );

    // build variables for bex api
    const variables = Object.keys(hotelsGroupedByDates)
      .filter((dates) => hotelsGroupedByDates[dates].length > 0)
      .map((dates) => {
        const hotels = hotelsGroupedByDates[dates];
        const hotelIds = hotels
          .map((hotel) => (hotel.productInfo as HistoryPropertyDetailInlineFragment)?.propertyId)
          .filter((id) => typeof id === "string") as string[];
        const { startDate, endDate } = hotels[0];

        return {
          context: getBexApiContext(contextStore),
          hotelIds,
          dateRange: this.formatDate(startDate, endDate),
        };
      });

    try {
      // prepare promises and call bex api to get fresh data in parallel
      return await Promise.all(
        variables.map((variable) => this.bexApiService.fetchCustomerPropertySearchByIdQuery(variable))
      );
    } catch (err) {
      this.logger.logEvent(FAILED_HOTEL_FETCH, "Unexpected error when fetching customer property search by id", err);
      throw err;
    }
  }

  @action
  public async fetchRecentlyViewedHotels(contextStore: ExtendedContextStore, numberOfResults: number) {
    const variables: CustomerSearchHistoryQuery.Variables = {
      context: getBexApiContext(contextStore),
      lobs: [HistoryType.PROPERTY_DETAIL],
      numberOfResults,
    };

    try {
      // get recently viewed hotels
      const query = await this.bexApiService.fetchCustomerSearchHistoryQuery(variables);

      if (query.errors) {
        return [];
      }

      const recentlyViewedHotels = query.data?.customer?.searchHistory
        ?.reduce((acc, searchHistoryItemsArray) => [...acc, ...searchHistoryItemsArray.items!], [])
        .filter((item) => item) as Items[];

      if (!recentlyViewedHotels?.length) {
        return [];
      }

      // get same hotels with fresh data
      const freshHotelData = await this.fetchUpdatedRecentlyViewedHotels(recentlyViewedHotels, contextStore);

      if (!freshHotelData?.length) {
        return [];
      }

      // merge together and return
      return this.getFreshRecentlyViewedHotels(
        recentlyViewedHotels,
        freshHotelData.filter((data) => !data.errors)
      );
    } catch (err) {
      this.logger.logEvent(FAILED_HOTEL_FETCH, "Unexpected error when fetching recently viewed hotels", err);
      throw err;
    }
  }

  @action
  public setAllHotelsOnMapOnHotelsByLatLongSuccessResponse(propertySearch: PropertySearch, locationId: string) {
    const newHotels: MapPlace[] = [];
    // Get search date range to determine pricing of hotels near the user
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    // Format check in and check out date to be what PropertyForMap expects
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;
    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    // Check if the hotel/property has all the necessary info to be displayed on the map
    for (const property of propertySearch.properties) {
      if (property.mapMarker && property.mapMarker.latLong && property.infositeURL) {
        const newHotel: MapPlace = {
          id: property.id,
          placeName: property.name,
          formattedPrice:
            property.price && property.price.lead && property.price.lead.amount
              ? property.price.lead.formatted
              : undefined,
          latitude: property.mapMarker.latLong.latitude,
          longitude: property.mapMarker.latLong.longitude,
          overallReviewRating: formatText("maps.sheets.hotel.reviewRating.text", property.reviews.score),
          reviewCount: `(${formatText("maps.sheets.hotel.reviews.text", property.reviews.total)})`,
          localizedSubtitle: property.reviews.localizedSubtitle,
          imageUrl: property.image && property.image.url,
          hrefUrl: this.constructPinnedHSRUrl(locationId, property.id, checkInDate, checkOutDate),
          icon: {
            type: "price",
          },
        };

        newHotels.push(newHotel);
      }
    }

    // Replace existing properties
    this.allHotelsOnMap = newHotels;
  }

  /**
   * Getter for allHotelsOnMap. Filters through them to prevent the bug on BEX
   * API/our data where we sometimes have hotels on in the middle of the ocean
   * (0,0).
   * @readonly
   * @type {MapPlace[]}
   */
  @computed
  public get hotelsOnMap(): MapPlace[] {
    return this.allHotelsOnMap.filter((hotel: MapPlace) => hotel.latitude !== 0 || hotel.longitude !== 0);
  }

  /**
   * This method converts a hotel coming from the flex composition into a hotel that can be placed on the map.
   * @param hotel A hotel coming from the composition.
   * @return a hotel for map
   */
  public mapFlexHotelToPropertyForMap(hotel: Hotel, contextStore: ExtendedContextStore, locationId: string): MapPlace {
    const searchDateRange: PropertyDateRangeInput = this.getSearchDateRange();
    const checkInDate = `${searchDateRange.checkInDate.year}-${searchDateRange.checkInDate.month}-${searchDateRange.checkInDate.day}`;

    const checkOutDate = `${searchDateRange.checkOutDate.year}-${searchDateRange.checkOutDate.month}-${searchDateRange.checkOutDate.day}`;

    const language = contextStore.locale.split(/[_-]/);

    let overallReviewRating;
    if (hotel.hotelOverallReviewRating) {
      overallReviewRating = formatText(
        "maps.sheets.hotel.reviewRating.text",
        hotel.hotelOverallReviewRating.toLocaleString(language, { maximumFractionDigits: 1 })
      );
    }

    let reviewCount;
    if (hotel.reviewCount) {
      reviewCount = `(${formatText(
        "maps.sheets.hotel.reviews.text",
        hotel.reviewCount.toLocaleString(language, { maximumFractionDigits: 1 })
      )})`;
    }

    return {
      id: hotel.hotelId,
      placeName: hotel.hotelName,
      formattedPrice: hotel.formattedPrice,
      latitude: hotel.hotelLatitude,
      longitude: hotel.hotelLongitude,
      overallReviewRating,
      reviewCount,
      imageUrl: hotel.hotelImageUrl,
      hrefUrl: this.constructPinnedHSRUrl(locationId, hotel.hotelId, checkInDate, checkOutDate),
      icon: {
        type: hotel.formattedPrice ? "price" : "soldOut",
      },
    };
  }

  public hydrate(data: SerializedData): void {
    // The serialized store as returned by the server exposes a stripped down
    // version of bexApiService with no methods.We delete it here to prevent the override.
    delete data.bexApiService;

    Object.assign(this, data);
  }

  /**
   * Calculate and return search date range for hotels we want to find around
   * our user. We choose check-in on a Friday two weeks from now. Note that the
   * range is not based on what the user input in the Wizard, so it's not dynamic
   * @returns {PropertyDateRangeInput} - Check in date and check out dates
   */
  public getSearchDateRange(): PropertyDateRangeInput {
    //Check-in is Friday two weeks from now
    //Check out is the day after that
    const today = new Date();
    const checkInDate = getNextFriday(today);
    const checkOutDate = new Date(checkInDate);
    checkOutDate.setDate(checkInDate.getDate() + 1);

    return {
      checkInDate: {
        day: checkInDate.getDate(),
        month: checkInDate.getMonth() + 1,
        year: checkInDate.getFullYear(),
      },
      checkOutDate: {
        day: checkOutDate.getDate(),
        month: checkOutDate.getMonth() + 1,
        year: checkOutDate.getFullYear(),
      },
    };
  }

  public constructPinnedHSRUrl(regionId: string, hotelId: string, startDate: string, endDate: string) {
    return `/Hotel-Search?selected=${hotelId}&PinnedHotelID=${hotelId}&HadPinnedHotel=true&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}`;
  }

  public formatDate(startDate: DateInput, endDate: DateInput): PropertyDateRangeInput {
    return {
      checkInDate: {
        day: startDate.day,
        month: startDate.month,
        year: startDate.year,
      },
      checkOutDate: {
        day: endDate.day,
        month: endDate.month,
        year: endDate.year,
      },
    };
  }
}
